import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box, Paper, Typography, IconButton, Tooltip, Alert, Avatar,
  Dialog, DialogTitle, DialogContent, DialogActions, Button
} from '@mui/material';
import {
  KeyboardDoubleArrowRight as CollapseIcon,
  DeleteOutline as DeleteIcon,
  OpenInFull as ExpandIcon,
  CloseFullscreen as ShrinkIcon
} from '@mui/icons-material';
import * as tassApi from '../../../services/tassApi';
import TassChatArea from './TassChatArea';
import TassInputBar from './TassInputBar';

function TassPanel({ token, open, onToggle, viewContext, onNavigateToClient, proxyTherapistId, hideCollapsedLauncher = false }) {
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [reviewJob, setReviewJob] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const sessionInitialized = useRef(false);
  const lastSyncedContext = useRef(null);

  // Esc key exits expanded mode
  useEffect(() => {
    if (!expanded) return;
    const handleKey = (e) => { if (e.key === 'Escape') setExpanded(false); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [expanded]);

  // Initialize or resume session when panel opens
  useEffect(() => {
    if (!open || !token || sessionInitialized.current) return;

    const initSession = async () => {
      try {
        // Try to get an existing active session first
        const activeSession = await tassApi.getActiveSession(token);
        if (activeSession && activeSession.session) {
          setSessionId(activeSession.session._id);
          // Restore messages from the active task
          const session = activeSession.session;
          if (session.tasks && session.tasks.length > 0) {
            const currentTask = session.tasks[session.currentTaskIndex] || session.tasks[session.tasks.length - 1];
            if (currentTask.messages) {
              setMessages(currentTask.messages);
            }
          }
          lastSyncedContext.current = viewContext ? { ...viewContext } : null;
          sessionInitialized.current = true;
          return;
        }
      } catch (err) {
        // No active session found — create a new one
      }

      try {
        const newSession = await tassApi.createSession(viewContext || {}, token, proxyTherapistId || null);
        if (newSession && newSession.session) {
          setSessionId(newSession.session._id);
          lastSyncedContext.current = viewContext ? { ...viewContext } : null;
          sessionInitialized.current = true;
        }
      } catch (err) {
        console.error('[TassPanel] Failed to create session:', err);
        setError('Failed to start TASS session. Please try again.');
      }
    };

    initSession();
  }, [open, token, viewContext]);

  // Send a message to TASS
  const handleSend = useCallback(async (text) => {
    if (!text.trim() || !sessionId || isLoading) return;

    setError('');
    setIsLoading(true);

    // Add therapist message optimistically
    const therapistMsg = {
      _id: 'temp-' + Date.now(),
      role: 'therapist',
      content: text,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, therapistMsg]);

    try {
      const response = await tassApi.sendMessage(sessionId, text, token);

      // Build new messages from the response
      const newMessages = [];

      // Replace temp therapist message with server version if provided
      const serverTherapistMsg = response.therapistMessage || therapistMsg;
      newMessages.push({
        ...serverTherapistMsg,
        role: 'therapist',
        content: serverTherapistMsg.content || text,
        timestamp: serverTherapistMsg.timestamp || therapistMsg.timestamp
      });

      // Handle task transition boundary
      if (response.taskTransition) {
        newMessages.push({
          _type: 'task_boundary',
          topic: response.taskTransition.topic,
          timestamp: new Date()
        });
      }

      // Add TASS response
      if (response.tassMessage) {
        newMessages.push({
          _id: response.tassMessage._id || 'tass-' + Date.now(),
          role: 'tass',
          content: response.tassMessage.content,
          timestamp: response.tassMessage.timestamp || new Date(),
          toolCalls: response.tassMessage.toolCalls || [],
          sourceCitations: response.tassMessage.sourceCitations || [],
          confidence: response.tassMessage.confidence,
          conversationSegments: response.tassMessage.conversationSegments || null,
          pendingConfirmation: response.tassMessage.pendingConfirmation || null
        });
      }

      // Check for pending review jobs
      if (response.pendingReviewJob) {
        setReviewJob({
          jobId: response.pendingReviewJob.jobId,
          status: 'processing',
          onComplete: (result) => {
            setReviewJob(null);
            // Add a system message about completion
            setMessages(prev => [...prev, {
              _id: 'review-complete-' + Date.now(),
              role: 'system',
              content: `Client review completed. Ask TASS about the results.`,
              timestamp: new Date()
            }]);
          }
        });
      }

      // Replace temp message and add new ones
      setMessages(prev => {
        const filtered = prev.filter(m => m._id !== therapistMsg._id);
        return [...filtered, ...newMessages];
      });
    } catch (err) {
      console.error('[TassPanel] Send error:', err);
      setError('Failed to send message. Please try again.');
      // Remove temp message on error
      setMessages(prev => prev.filter(m => m._id !== therapistMsg._id));
    } finally {
      setIsLoading(false);
    }
  }, [sessionId, isLoading, token]);

  // Handle tool confirmation
  const handleConfirmTool = useCallback(async (toolCallId, approved) => {
    if (!sessionId) return;
    try {
      const response = await tassApi.confirmTool(sessionId, toolCallId, approved, token);

      // Remove pending confirmation from the message
      setMessages(prev => prev.map(m => {
        if (m.pendingConfirmation && m.pendingConfirmation.toolCallId === toolCallId) {
          return { ...m, pendingConfirmation: null };
        }
        return m;
      }));

      // Add the TASS follow-up response if any
      if (response.tassMessage) {
        setMessages(prev => [...prev, {
          _id: response.tassMessage._id || 'tass-confirm-' + Date.now(),
          role: 'tass',
          content: response.tassMessage.content,
          timestamp: response.tassMessage.timestamp || new Date(),
          toolCalls: response.tassMessage.toolCalls || [],
          sourceCitations: response.tassMessage.sourceCitations || [],
          confidence: response.tassMessage.confidence
        }]);
      }
    } catch (err) {
      console.error('[TassPanel] Confirm tool error:', err);
      setError('Failed to process confirmation.');
    }
  }, [sessionId, token]);

  // Sync viewContext changes to the server
  useEffect(() => {
    if (!sessionId || !viewContext) return;

    const prev = lastSyncedContext.current;
    const clientChanged = prev && prev.selectedClientId !== viewContext.selectedClientId;
    const changed = !prev
      || clientChanged
      || prev.activeView !== viewContext.activeView;

    if (!changed) return;

    lastSyncedContext.current = { ...viewContext };

    tassApi.updateViewContext(sessionId, viewContext, token)
      .then(response => {
        // If the server performed a task switch, insert a visual boundary
        if (response?.taskSwitch && messages.length > 0) {
          setMessages(prev => [...prev, {
            _type: 'task_boundary',
            topic: response.taskSwitch.topic,
            timestamp: new Date()
          }]);
        }
      })
      .catch(err => {
        console.error('[TassPanel] Failed to sync viewContext:', err);
      });
  }, [sessionId, viewContext, token, messages.length]);

  // Handle client selection from clickable names in messages — navigate instead of sending message
  const handleSelectClient = useCallback((clientId, clientName) => {
    if (onNavigateToClient) {
      onNavigateToClient(clientId, clientName);
    }
  }, [onNavigateToClient]);

  // Handle feedback
  const handleFeedback = useCallback(async (messageIndex, feedbackText) => {
    if (!sessionId) return;
    try {
      await tassApi.submitFeedback(sessionId, messageIndex, null, feedbackText, token);
    } catch (err) {
      console.error('[TassPanel] Feedback error:', err);
    }
  }, [sessionId, token]);

  // Collapsed state — show just the TASS icon button
  if (!open) {
    // Ticket F7 — when the sidebar shell is active, the docked sidebar TASS pin
    // (+ ⌘J) is the single launcher; don't also render the legacy fixed-right
    // collapsed rail (it would be a duplicate launcher).
    if (hideCollapsedLauncher) {
      return null;
    }
    return (
      <Box
        sx={{
          position: 'fixed',
          right: 0,
          top: 64,
          width: 48,
          height: 'calc(100vh - 64px)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pt: 1,
          bgcolor: 'background.paper',
          borderLeft: '1px solid',
          borderColor: 'divider',
          zIndex: 1200
        }}
      >
        <Tooltip title="Open Tass" placement="left">
          <IconButton
            onClick={onToggle}
            aria-label="Open TASS panel"
            sx={{
              p: 0,
              '&:hover': { transform: 'scale(1.08)' },
              transition: 'transform 0.15s ease'
            }}
          >
            <Avatar
              src="/tass-avatar.jpg?v=2"
              sx={{ width: 40, height: 40 }}
              alt="Tass"
            />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  // Expanded state
  const panelWidth = expanded ? 'min(900px, calc(100vw - 120px))' : 380;

  return (
    <>
      {/* Backdrop when expanded */}
      {expanded && (
        <Box
          onClick={() => setExpanded(false)}
          sx={{
            position: 'fixed',
            inset: 0,
            top: 64,
            bgcolor: 'rgba(0,0,0,0.3)',
            zIndex: 1199,
            transition: 'opacity 0.3s'
          }}
        />
      )}
      <Paper
        elevation={expanded ? 8 : 4}
        sx={{
          position: 'fixed',
          right: 0,
          top: 64,
          width: panelWidth,
          height: 'calc(100vh - 64px)',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 1200,
          borderLeft: '1px solid',
          borderColor: 'divider',
          borderRadius: 0,
          transition: 'width 0.3s ease'
        }}
      >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.5,
          py: 1,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'primary.main',
          color: 'white'
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Avatar
            src="/tass-avatar.jpg?v=2"
            onClick={() => setProfileOpen(true)}
            sx={{
              width: 30,
              height: 30,
              cursor: 'pointer',
              '&:hover': { boxShadow: '0 0 0 2px rgba(255,255,255,0.5)' },
              transition: 'box-shadow 0.15s ease'
            }}
            alt="Tass"
          />
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Tass - your assistant
            </Typography>
            {viewContext?.selectedClientName && (
              <Typography variant="caption" sx={{ opacity: 0.85, display: 'block', lineHeight: 1.2, mt: -0.3 }}>
                Focused on: {viewContext.selectedClientName}
              </Typography>
            )}
          </Box>
        </Box>
        <Box>
          <Tooltip title="End session">
            <IconButton
              size="small"
              onClick={() => setDeleteConfirmOpen(true)}
              aria-label="End session"
              sx={{ color: 'white', opacity: 0.8, '&:hover': { opacity: 1 } }}
            >
              <DeleteIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={expanded ? 'Shrink (Esc)' : 'Expand'}>
            <IconButton
              size="small"
              onClick={() => setExpanded(prev => !prev)}
              aria-label={expanded ? 'Shrink TASS panel' : 'Expand TASS panel'}
              sx={{ color: 'white', opacity: 0.8, '&:hover': { opacity: 1 } }}
            >
              {expanded ? <ShrinkIcon sx={{ fontSize: 17 }} /> : <ExpandIcon sx={{ fontSize: 17 }} />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Collapse">
            <IconButton size="small" onClick={() => { setExpanded(false); onToggle(); }} aria-label="Collapse TASS panel" sx={{ color: 'white' }}>
              <CollapseIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Error bar */}
      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ borderRadius: 0 }}>
          {error}
        </Alert>
      )}

      {/* Chat Area */}
      <TassChatArea
        messages={messages}
        isLoading={isLoading}
        onConfirmTool={handleConfirmTool}
        onFeedback={handleFeedback}
        onSelectClient={handleSelectClient}
        reviewJob={reviewJob}
        token={token}
      />

      {/* Input Bar */}
      <TassInputBar onSend={handleSend} disabled={isLoading} />

      {/* End Session Confirmation */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="xs"
      >
        <DialogTitle>End TASS session?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This will clear the current conversation. You can start a new session at any time.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} size="small">
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            size="small"
            onClick={async () => {
              setDeleteConfirmOpen(false);
              if (sessionId) {
                try {
                  await tassApi.closeSession(sessionId, token);
                } catch (err) {
                  console.error('[TassPanel] Close session error:', err);
                }
              }
              setSessionId(null);
              setMessages([]);
              sessionInitialized.current = false;
              onToggle();
            }}
          >
            End session
          </Button>
        </DialogActions>
      </Dialog>

      {/* Profile Popup */}
      <Dialog
        open={profileOpen}
        onClose={() => setProfileOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
      >
        <Box
          sx={{
            background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
            pt: 3,
            pb: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
        >
          <Avatar
            src="/tass-avatar.jpg?v=2"
            sx={{ width: 120, height: 120, mb: 1.5, boxShadow: 3 }}
            alt="Tass"
          />
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>
            Tass
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)' }}>
            Your Clinical Assistant
          </Typography>
        </Box>
        <DialogContent sx={{ pt: 2.5, pb: 3 }}>
          <Typography variant="body2" sx={{ mb: 1.5, lineHeight: 1.6 }}>
            Hi! I'm <strong>Tass</strong>, your dedicated clinical support assistant on the AIMHI platform. I'm here to help you manage your caseload efficiently so you can focus on what matters most — your clients.
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5, lineHeight: 1.6, fontWeight: 600 }}>
            What I can help with:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5, mb: 1.5 }}>
            {[
              'Reviewing client progress and flagging concerns',
              'Summarising sessions and assessment trends',
              'Running clinical reviews across your caseload',
              'Prioritising which clients need your attention',
              'Creating care notes and session summaries'
            ].map((item, i) => (
              <Typography key={i} component="li" variant="body2" sx={{ mb: 0.5, lineHeight: 1.5 }}>
                {item}
              </Typography>
            ))}
          </Box>
          <Typography variant="body2" sx={{ lineHeight: 1.6, color: 'text.secondary' }}>
            I'll always cite my data sources and express uncertainty when I'm not sure. Every suggestion I make is advisory — you're always in control of clinical decisions.
          </Typography>
        </DialogContent>
      </Dialog>
    </Paper>
    </>
  );
}

export default TassPanel;
